import { makeRequest, API_BASE_URL } from "./client";
import type { School } from "./types";

export const authApi = {
  login: async (
    email: string,
    password: string
  ): Promise<
    | {
        message: string;
        tokens: {
          accessToken: string;
          idToken: string;
          refreshToken: string;
        };
        organization?: {
          oid: string;
          soid?: string;
          name: string;
          email: string;
          isApproved: boolean;
          tier: string;
          sponsored?: boolean;
          isPublic: string;
          logo: string;
          description?: string;
          type: string;
          createdAt: string;
          PK: string;
          SK: string;
          [key: string]: any;
        };
        school?: {
          schoolId: string;
          soid?: string;
          schoolName: string;
          email: string;
          isSchoolApproved: boolean;
          tier: string;
          sponsored?: boolean;
          isCircleOpen: string;
          schoolLogo: string;
          type: string;
          createdAt: string;
          PK: string;
          SK: string;
          [key: string]: any;
        };
      }
    | {
        error: string;
        details: string;
      }
  > => {
    const response = await fetch(`${API_BASE_URL}/v1/org/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    // Check if response contains an error field (backend error response)
    if (data.error) {
      return data;
    }

    if (!response.ok) {
      return {
        error: data.error || "Login failed",
        details:
          data.details ||
          "Please check your email and password and try again.",
      };
    }

    // Map the new organization response back to the old school structure for frontend compatibility
    return {
      ...data,
      school: {
        schoolId: data.organization.oid,
        soid: data.organization.soid,
        schoolName: data.organization.name,
        email: data.organization.email,
        isSchoolApproved: data.organization.isApproved,
        tier: data.organization.tier,
        sponsored: data.organization.sponsored === true,
        isCircleOpen: data.organization.isPublic,
        schoolLogo: data.organization.logo,
        type: data.organization.type,
        createdAt: data.organization.createdAt,
        PK: data.organization.PK,
        SK: data.organization.SK,
      },
    };
  },

  // Team member login (username OR phone number)
  teamLogin: async (
    usernameOrPhone: string,
    pin: string
  ): Promise<
    | {
        success: boolean;
        token: string;
        user: {
          username: string;
          displayName: string;
          accessLevel: string;
          orgId: string;
          circleIds: string[];
          phoneNumber?: string;
          phoneVerified: boolean;
          tier?: string;
          sponsored?: boolean;
          featureFlags?: Record<string, boolean>;
          soid?: string;
        };
      }
    | {
        error: string;
        details?: string;
      }
  > => {
    // Determine if input is phone number or username
    const isPhoneNumber = /^[\d\s+\-()]+$/.test(
      usernameOrPhone.replace(/\s/g, "")
    );

    const requestBody = isPhoneNumber
      ? { phoneNumber: usernameOrPhone, pin }
      : { username: usernameOrPhone, pin };

    const response = await fetch(`${API_BASE_URL}/v1/team/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    // Check if response contains an error field (backend error response)
    if (data.error) {
      return data;
    }

    // If no error, check if response is successful
    if (!response.ok) {
      throw new Error(
        data.message || `API request failed: ${response.statusText}`
      );
    }

    return data;
  },

  signup: async (
    orgName: string,
    email: string,
    password: string,
    type?: "organization" | "school" | "church" | "individual"
  ): Promise<{
    message: string;
    schoolId: string;
    soid?: string;
    email: string;
    schoolName: string;
  }> => {
    const response = await makeRequest<{
      message: string;
      oid: string;
      soid: string;
      email: string;
      name: string;
    }>(
      "/v1/org/auth/signup",
      {
        method: "POST",
        body: JSON.stringify({ name: orgName, email, password, type: type ?? "school" }),
      },
      API_BASE_URL
    );

    // Map the new organization response back to the old school structure for frontend compatibility
    return {
      message: response.message,
      schoolId: response.oid,
      soid: response.soid,
      email: response.email,
      schoolName: response.name,
    };
  },

  verifyAccount: async (
    email: string,
    confirmationCode: string
  ): Promise<{ message: string; details: string; email: string }> => {
    return makeRequest(
      "/v1/org/auth/confirm-signup",
      {
        method: "POST",
        body: JSON.stringify({ email, confirmationCode }),
      },
      API_BASE_URL
    );
  },

  resendVerification: async (
    email: string
  ): Promise<{ message: string; details: string }> => {
    return makeRequest(
      "/v1/org/auth/resend-verification",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
      API_BASE_URL
    );
  },

  createSchool: async (
    schoolData: Omit<School, "id" | "isApproved">
  ): Promise<School> => {
    return makeRequest("/v1/org", {
      method: "POST",
      body: JSON.stringify(schoolData),
    });
  },

  resetPassword: async (email: string): Promise<void> => {
    return makeRequest(
      "/v1/org/auth/reset-password",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
      API_BASE_URL
    );
  },

  confirmPasswordReset: async (
    email: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<{ message: string; details: string }> => {
    return makeRequest(
      "/v1/org/auth/confirm-password-reset",
      {
        method: "POST",
        body: JSON.stringify({ email, confirmationCode, newPassword }),
      },
      API_BASE_URL
    );
  },

  linkUserToSchool: async (
    schoolId: string,
    email: string,
    token: string,
    accessLegacyMessages: boolean = false,
    legacyAccessDays?: number,
    alias?: string
  ): Promise<{
    message: string;
    status?: "pending" | "linked";
    invitationId?: string;
    userId?: string;
  }> => {
    const body: any = {
      email: email.toLowerCase(),
      accessLegacyMessages,
    };

    if (legacyAccessDays !== undefined) {
      body.legacyAccessDays = legacyAccessDays;
    }

    const aliasTrimmed = alias?.trim();
    if (aliasTrimmed) {
      body.alias = aliasTrimmed;
    }

    const response = await makeRequest<{
      message: string;
      status?: "pending" | "linked";
      invitationId?: string;
      userId?: string;
    }>(`/v1/org/${schoolId}/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    if (!response) {
      throw new Error("Failed to link user to organization");
    }

    return response;
  },

  linkUserToSchoolByPhone: async (
    schoolId: string,
    phoneNumber: string,
    token: string,
    accessLegacyMessages: boolean = false,
    legacyAccessDays?: number,
    alias?: string
  ): Promise<{
    message: string;
    status?: "pending" | "linked";
    invitationId?: string;
    userId?: string;
  }> => {
    const { normalizePhoneNumber } = await import("../../utils/phoneUtils");
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    const body: any = {
      phoneNumber: normalizedPhone,
      accessLegacyMessages,
    };

    if (legacyAccessDays !== undefined) {
      body.legacyAccessDays = legacyAccessDays;
    }

    const aliasTrimmed = alias?.trim();
    if (aliasTrimmed) {
      body.alias = aliasTrimmed;
    }

    const response = await makeRequest<{
      message: string;
      status?: "pending" | "linked";
      invitationId?: string;
      userId?: string;
    }>(`/v1/org/${schoolId}/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    if (!response) {
      throw new Error("Failed to link user to organization");
    }

    return response;
  },

  linkUserToSchoolByUsername: async (
    schoolId: string,
    username: string,
    token: string
  ): Promise<void> => {
    const response = await makeRequest<{ message: string }>(
      `/v1/org/${schoolId}/users`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username }),
      }
    );

    if (!response) {
      throw new Error("Failed to link user to organization");
    }
  },
};
